import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ShoppingCartIcon, HeartIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

const ProductQuickView = ({ product, isOpen, onClose, onAddToCart, onAddToWishlist }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!product) return null;

  // Handle both external URLs (Unsplash) and internal paths
  const rawImages = (product.images || []).filter(img => img && typeof img === 'string');
  const images = rawImages.length > 0
    ? rawImages.map(img => img.startsWith('http') ? img : `/api${img}`)
    : ['/placeholder-product.jpg'];
  
  const fragranceNotes = product.attributes?.fragrance_notes || {
    top: ['ברגמוט', 'לימון'],
    heart: ['ורד', 'יסמין'],
    base: ['ענבר', 'מאסק']
  };

  const handleAddToCart = () => {
    onAddToCart && onAddToCart(product, quantity);
  };

  const handleAddToWishlist = () => {
    onAddToWishlist && onAddToWishlist(product);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-right align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 hover:bg-gray-100 transition-colors"
                    aria-label="סגור"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900">צפייה מהירה</h3>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
                  {/* Left: Images */}
                  <div className="space-y-4">
                    <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={images[selectedImage]}
                        alt={product.name}
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                    
                    {/* Thumbnail images */}
                    {images.length > 1 && (
                      <div className="grid grid-cols-4 gap-2">
                        {images.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImage(index)}
                            className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-md ${
                              selectedImage === index
                                ? 'ring-2 ring-primary-500'
                                : 'ring-1 ring-gray-200'
                            }`}
                          >
                            <img
                              src={image}
                              alt={`${product.name} ${index + 1}`}
                              className="h-full w-full object-cover object-center"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right: Product info */}
                  <div className="flex flex-col">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h2>
                      
                      {/* Rating */}
                      {product.rating_average > 0 && (
                        <div className="flex items-center mb-4">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <StarIconSolid
                                key={i}
                                className={`h-5 w-5 ${
                                  i < Math.floor(product.rating_average)
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-gray-600 mr-2">
                            ({product.rating_count} ביקורות)
                          </span>
                        </div>
                      )}

                      {/* Price */}
                      <div className="mb-6">
                        <span className="text-3xl font-bold text-gray-900">
                          ₪{product.price_b2b || product.price_b2c}
                        </span>
                        {product.price_b2b && product.price_b2c > product.price_b2b && (
                          <span className="text-lg text-gray-500 line-through mr-2">
                            ₪{product.price_b2c}
                          </span>
                        )}
                      </div>

                      {/* Short description */}
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {product.short_description || product.description?.substring(0, 150)}
                      </p>

                      {/* Fragrance Notes */}
                      <div className="mb-6 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                        <h4 className="font-semibold text-gray-900 mb-3">תווי ניחוח</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <span className="inline-block w-20 text-sm font-medium text-purple-700">ראש:</span>
                            <span className="text-sm text-gray-700">{fragranceNotes.top?.join(', ')}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="inline-block w-20 text-sm font-medium text-pink-700">לב:</span>
                            <span className="text-sm text-gray-700">{fragranceNotes.heart?.join(', ')}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="inline-block w-20 text-sm font-medium text-amber-700">בסיס:</span>
                            <span className="text-sm text-gray-700">{fragranceNotes.base?.join(', ')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Quantity selector */}
                      <div className="flex items-center gap-4 mb-6">
                        <label className="text-sm font-medium text-gray-700">כמות:</label>
                        <div className="flex items-center border border-gray-300 rounded-lg">
                          <button
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            className="px-3 py-2 hover:bg-gray-100 transition-colors"
                          >
                            -
                          </button>
                          <span className="px-4 py-2 font-semibold">{quantity}</span>
                          <button
                            onClick={() => setQuantity(quantity + 1)}
                            className="px-3 py-2 hover:bg-gray-100 transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-auto space-y-3">
                      <button
                        onClick={handleAddToCart}
                        className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
                      >
                        <ShoppingCartIcon className="h-5 w-5 ml-2" />
                        הוסף לעגלה
                      </button>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={handleAddToWishlist}
                          className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-600 transition-colors"
                        >
                          <HeartIcon className="h-5 w-5 ml-2" />
                          רשימת משאלות
                        </button>
                        
                        <Link
                          to={`/products/${product.id}`}
                          className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-600 transition-colors"
                          onClick={onClose}
                        >
                          פרטים מלאים
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

ProductQuickView.propTypes = {
  product: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func,
  onAddToWishlist: PropTypes.func,
};

export default ProductQuickView;

